/* eslint-disable no-console */
import { removeSurface } from './player.utils';

/**
 * Owns one media engine and its DOM nodes at a time.
 *
 * Loads are queued, so a released engine is torn down before the next one claims the nodes,
 * and a load which became stale on its way (i.e. released meanwhile) is dropped.
 */
export class PlaybackController {
  /**
   * @param {Object} setup
   * @param {Function} setup.loadEngine (params) => Promise of the engine class
   * @param {Function} setup.createSurface (params) => `{ element, container }`, `container` is removed on release
   * @param {Function} setup.getOptions (params, surface, Engine) => engine options, or a Promise of them
   */
  constructor(setup) {
    this._setup = setup;
    this._generation = 0;
    this._queue = Promise.resolve();
    this._pending = null;
    this._engine = null;
    this._surface = null;
    this._params = null;
  }

  get engine() {
    return this._engine;
  }

  get element() {
    return this._surface ? this._surface.element : null;
  }

  get params() {
    return this._params;
  }

  get isLoading() {
    return Boolean(this._pending);
  }

  /**
   * Warms up the engine module only
   */
  preload(params) {
    return Promise.resolve(this._setup.loadEngine(params)).catch(error =>
      console.error('[PlaybackController] Could not preload the engine', error),
    );
  }

  /**
   * @returns {Promise} resolves with the engine once it is usable, or with null when the load was dropped
   */
  load(params = {}) {
    const generation = this._generation;
    const isStale = () => generation !== this._generation;

    this._params = params;

    const pending = this._queue
      .then(async () => {
        if (isStale()) {
          return null;
        }

        const Engine = await this._setup.loadEngine(params);
        if (isStale()) {
          return null;
        }

        this._releaseCurrent();
        const surface = this._setup.createSurface(params);
        const engine = new Engine();
        engine.configure(await this._setup.getOptions(params, surface, Engine));
        engine.init(surface.element);

        if (isStale()) {
          console.log('[PlaybackController] Engine is no longer needed, releasing it');
          engine.release();
          removeSurface(surface.container);

          return null;
        }

        this._engine = engine;
        this._surface = surface;

        return engine;
      })
      .catch(error => {
        console.error('[PlaybackController] Could not load the engine', error);

        return null;
      })
      .then(engine => {
        if (this._pending === pending) {
          this._pending = null;
        }

        return engine;
      });

    this._pending = pending;
    this._queue = pending;

    return pending;
  }

  /**
   * @returns {Promise} resolves with the engine being loaded, or with the current one
   */
  whenReady() {
    return this._pending || Promise.resolve(this._engine);
  }

  /**
   * Releases the current engine and its nodes, and drops the pending load if any
   */
  release() {
    this._generation += 1;
    this._pending = null;
    this._params = null;
    this._releaseCurrent();
  }

  _releaseCurrent() {
    const engine = this._engine;
    const surface = this._surface;

    this._engine = null;
    this._surface = null;

    if (engine) {
      engine.release();
    }

    if (surface) {
      removeSurface(surface.container);
    }
  }
}
